import { Card, CardContent, MetricInfoPopover } from '@/components/ui';
import type { ITeamQualityRow } from '@skystone/shared';
import { cn } from '@/lib/utils';
import {
  passRate,
  passRateColorClass,
  teamsForPassRate,
} from './team-row-formatter';

interface QgPassRateByTeamPanelProps {
  teams: ReadonlyArray<ITeamQualityRow>;
}

/**
 * "Quality Gate Pass Rate by Team" panel — right half of the team-breakouts
 * row at `lg+`.
 *
 * Lists every in-scope team. Sort: pass rate desc, alphabetical tie-break.
 * The raw `M / N` cell drops at `base/sm`; the percentage cell stays as the
 * primary signal. Pass-rate colour follows the `cq_qg_pass_rate` tier
 * thresholds seeded by US7.
 */
export function QgPassRateByTeamPanel({ teams }: Readonly<QgPassRateByTeamPanelProps>) {
  const rows = teamsForPassRate(teams);

  return (
    <Card className="shadow-none" data-testid="cq-qg-by-team-panel">
      <CardContent className="p-4">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold text-foreground">Quality Gate Pass Rate by Team</h3>
          <MetricInfoPopover metricKey="cq_qg_pass_rate" />
        </div>
        <p className="mt-1 text-xs italic text-muted-foreground">
          Repos passing the gate ÷ repos with a gate verdict.
        </p>
        {rows.length === 0 ? (
          <p
            className="mt-4 text-xs text-muted-foreground"
            data-testid="cq-qg-by-team-empty"
          >
            No teams in this scope yet.
          </p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="pb-2 pr-3 text-xs font-medium text-muted-foreground">Team</th>
                <th className="hidden pb-2 pr-3 text-xs font-medium text-muted-foreground text-right sm:table-cell">
                  Passing
                </th>
                <th className="pb-2 text-xs font-medium text-muted-foreground text-right">Pass rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((team) => {
                const rate = passRate(team);
                const pct = rate === null ? null : rate * 100;
                return (
                  <tr
                    key={team.teamId}
                    className="border-b border-border/40"
                    data-testid={`cq-qg-row-${team.teamId}`}
                  >
                    <td className="py-1.5 pr-3 text-xs">{team.teamName}</td>
                    <td className="hidden py-1.5 pr-3 text-right text-xs text-muted-foreground tabular-nums sm:table-cell">
                      {team.qgTotal === 0 ? '—' : `${team.qgPassed} / ${team.qgTotal}`}
                    </td>
                    <td
                      className={cn(
                        'py-1.5 text-right text-xs font-semibold tabular-nums',
                        passRateColorClass(rate),
                      )}
                    >
                      {pct === null ? '—' : `${pct.toFixed(1)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
